/**
 * Survey / Grid auto-mission generation.
 *
 * Given an area polygon (lat/lng vertices) plus a line spacing, sweep angle and
 * altitude, this produces a boustrophedon ("lawnmower") path of WAYPOINTs that
 * covers the polygon — the same idea as ArduPilot Mission Planner's Survey(Grid).
 *
 * The math is done in a local equirectangular projection (metres) centred on the
 * polygon so that `spacing` is a true ground distance. It is a pure function with
 * no React/Leaflet dependency so it can be unit-tested in isolation.
 */

const EARTH_M_PER_DEG_LAT = 110540; // metres per degree latitude (mean)
const EARTH_M_PER_DEG_LON = 111320; // metres per degree longitude at equator

/** Average of the polygon vertices — used as projection origin + rotation centre. */
function centroidOf(points) {
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

function makeProjection(origin) {
  const cosLat = Math.cos((origin.lat * Math.PI) / 180);
  return {
    toXY: ({ lat, lng }) => ({
      x: (lng - origin.lng) * EARTH_M_PER_DEG_LON * cosLat,
      y: (lat - origin.lat) * EARTH_M_PER_DEG_LAT,
    }),
    toLatLng: ({ x, y }) => ({
      lat: origin.lat + y / EARTH_M_PER_DEG_LAT,
      lng: origin.lng + x / (EARTH_M_PER_DEG_LON * cosLat),
    }),
  };
}

function rotate({ x, y }, angleRad) {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return { x: x * c - y * s, y: x * s + y * c };
}

/** X coordinates where the horizontal line y = scanY crosses the polygon edges. */
function scanlineIntersections(ring, scanY) {
  const xs = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    // Half-open test [min,max) avoids double-counting shared vertices.
    const crosses = (a.y <= scanY && b.y > scanY) || (b.y <= scanY && a.y > scanY);
    if (!crosses) continue;
    const t = (scanY - a.y) / (b.y - a.y);
    xs.push(a.x + t * (b.x - a.x));
  }
  return xs.sort((m, n) => m - n);
}

function pathLengthMetres(xyPoints) {
  let d = 0;
  for (let i = 1; i < xyPoints.length; i++) {
    d += Math.hypot(xyPoints[i].x - xyPoints[i - 1].x, xyPoints[i].y - xyPoints[i - 1].y);
  }
  return d;
}

/**
 * @param {Array<{lat:number,lng:number}>} polygon  Area boundary (>= 3 vertices).
 * @param {object} options
 * @param {number} [options.spacing]   Distance between sweep lines, metres (> 0).
 * @param {number} [options.overlap]   Side overlap %, 0–95. When set, spacing is derived
 *                                     from altitude + camera HFOV and `spacing` is ignored.
 * @param {number} [options.hfovDeg=60] Camera horizontal FOV, used with `overlap`.
 * @param {number} [options.altitude=50]  Waypoint altitude AGL, metres.
 * @param {number} [options.angleDeg=0]   Sweep heading, degrees (0 = W→E lines).
 * @returns {{ waypoints: Array, stats: { lineCount:number, waypointCount:number, lengthMeters:number, spacing:number } }}
 */
export function generateSurveyGrid(polygon, options = {}) {
  const altitude = options.altitude != null ? Number(options.altitude) : 50;
  const angleDeg = options.angleDeg != null ? Number(options.angleDeg) : 0;
  const spacing = options.overlap != null
    ? spacingFromCamera({
        altitude,
        hfovDeg: options.hfovDeg != null ? Number(options.hfovDeg) : 60,
        sideOverlap: Math.min(95, Math.max(0, Number(options.overlap))) / 100,
      })
    : Number(options.spacing);

  if (!Array.isArray(polygon) || polygon.length < 3) {
    throw new Error('Survey area needs at least 3 points.');
  }
  if (!Number.isFinite(spacing) || spacing <= 0) {
    throw new Error('Line spacing must be a positive number of metres.');
  }

  const origin = centroidOf(polygon);
  const proj = makeProjection(origin);
  const angleRad = (angleDeg * Math.PI) / 180;

  // Project to metres, then rotate by -angle so sweep lines become horizontal.
  const ring = polygon.map((p) => rotate(proj.toXY(p), -angleRad));

  const minY = Math.min(...ring.map((p) => p.y));
  const maxY = Math.max(...ring.map((p) => p.y));

  // Inset the first/last line by half a spacing so coverage is centred in the area.
  const path = []; // rotated XY points, in flight order
  let lineCount = 0;
  for (let i = 0, scanY = minY + spacing / 2; scanY < maxY; i++, scanY += spacing) {
    const xs = scanlineIntersections(ring, scanY);
    if (xs.length < 2) continue;
    // Outermost entry/exit for this line (robust for convex areas).
    let entry = { x: xs[0], y: scanY };
    let exit = { x: xs[xs.length - 1], y: scanY };
    // Boustrophedon: reverse every other line so the path zig-zags continuously.
    if (i % 2 === 1) [entry, exit] = [exit, entry];
    path.push(entry, exit);
    lineCount++;
  }

  const waypoints = path.map((pt, idx) => {
    const ll = proj.toLatLng(rotate(pt, angleRad)); // rotate back to true north
    return {
      id: `wp_survey_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 8)}`,
      type: 'WAYPOINT',
      latitude: Number(ll.lat.toFixed(7)),
      longitude: Number(ll.lng.toFixed(7)),
      altitude,
      frame: 3, // MAV_FRAME_GLOBAL_RELATIVE_ALT
      command: 16, // MAV_CMD_NAV_WAYPOINT
      param1: 0,
      param2: 0,
      param3: 0,
      param4: 0,
      autocontinue: 1,
    };
  });

  return {
    waypoints,
    stats: {
      lineCount,
      waypointCount: waypoints.length,
      lengthMeters: Math.round(pathLengthMetres(path)),
      spacing: Math.round(spacing * 10) / 10,
    },
  };
}

/**
 * Convert a desired forward/side photo overlap + camera footprint into a line
 * spacing, for callers that want to drive the grid from camera optics rather than
 * a raw spacing. footprintWidth = 2 * altitude * tan(HFOV/2).
 *
 * @returns {number} spacing in metres.
 */
export function spacingFromCamera({ altitude, hfovDeg = 60, sideOverlap = 0.7 }) {
  const footprint = 2 * altitude * Math.tan((hfovDeg * Math.PI) / 360);
  return Math.max(1, footprint * (1 - sideOverlap));
}

/**
 * Append a generated grid to a mission so it ends with exactly one RTL/LAND, which
 * validation requires. An existing RTL/LAND ending is kept and moved after the grid;
 * otherwise an RTL is added so the drone comes home. Editable later in the editor.
 */
export function appendSurveyToMission(existing, surveyWaypoints) {
  const base = Array.isArray(existing) ? existing : [];
  const last = base[base.length - 1];
  const keepEnd = !!last && (last.type === 'RTL' || last.type === 'LAND');
  const end = keepEnd ? last : {
    id: `wp_rtl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type: 'RTL',
    latitude: 0, // RTL flies to home at the vehicle's RTL_ALT; position/alt unused
    longitude: 0,
    altitude: 0,
    frame: 3,
    command: 20, // MAV_CMD_NAV_RETURN_TO_LAUNCH
    param1: 0,
    param2: 0,
    param3: 0,
    param4: 0,
    autocontinue: 1,
  };
  return [...(keepEnd ? base.slice(0, -1) : base), ...surveyWaypoints, end]
    .map((wp, index) => ({ ...wp, index }));
}
